import React, { useReducer, useEffect } from "react";
import { useIdleTimer } from "react-idle-timer";
import { Switch, Route, Redirect } from "react-router-dom";

import App from "./App";
import TrackAppointment from "./components/TrackAppointment";
import AppointmentConfirm from "./components/AppointmentConfirmForm";

import MFAEnRoll from "./components/SignIn/MFAEnroll";
import MFAVerify from "./components/SignIn/MFAVerification";

import { logCloseTabEvent } from "./services/userActivities";
import { initIdTokenCache } from "./utils/idTokenCache";

import { AppContext, DispatchContext } from "./state/Context";
import appReducer from "./state/reducers";
import trackAppointmentReducer from "./state/reducers/trackAppointment";
import INITITAL_STATE from "./state/InitialState";
import TRACK_APPOINTMENT_STATE from "./state/TrackAppointmentState";
import { TIMEOUT } from "./constants";
import { signOut } from "./utils/userActivity";
import PublicSecureFilePage from "./pages/PublicSecureFile";
import PublicGlobalSecureFilePage from "./pages/PublicGlobalSecureFile";

const AppContainer = () => {
  const [appState, dispatch] = useReducer(appReducer, INITITAL_STATE);
  const [taState, taDispatch] = useReducer(trackAppointmentReducer, TRACK_APPOINTMENT_STATE);

  useEffect(() => {
    const stopIdTokenCache = initIdTokenCache();
    let hasLoggedClose = false;

    // `pagehide` fires in cases `beforeunload` does not (tab discarded, mobile
    // backgrounding), so listen for both and de-duplicate. Neither handler may
    // await or call preventDefault: awaiting drops the request, and
    // preventDefault raises a "Leave site?" prompt on every navigation.
    const logCloseTabEventWhenPageUnload = () => {
      if (hasLoggedClose) return;
      hasLoggedClose = true;
      logCloseTabEvent();
      sessionStorage.removeItem("userActivitySessionId");
    };

    // The page can come back from the back/forward cache instead of unloading
    // for good, so allow its next close to be reported. App re-establishes the
    // activity session on the same event, since this one has just ended it.
    const resetOnPageShow = (event) => {
      if (event.persisted) {
        hasLoggedClose = false;
      }
    };

    window.addEventListener("pagehide", logCloseTabEventWhenPageUnload);
    window.addEventListener("beforeunload", logCloseTabEventWhenPageUnload);
    window.addEventListener("pageshow", resetOnPageShow);
    return () => {
      window.removeEventListener("pagehide", logCloseTabEventWhenPageUnload);
      window.removeEventListener("beforeunload", logCloseTabEventWhenPageUnload);
      window.removeEventListener("pageshow", resetOnPageShow);
      stopIdTokenCache();
    };
  }, []);

  const handleOnIdle = async () => {
    await signOut();
  };

  const { start } = useIdleTimer({
    timeout: TIMEOUT,
    onIdle: handleOnIdle,
    crossTab: true,
    startManually: true,
  });

  return (
    <Switch>
      <Route exact path="/">
        <Redirect to="/admin" />
      </Route>
      <Route path="/admin">
        <DispatchContext.Provider value={dispatch}>
          <AppContext.Provider value={appState}>
            <App start={start} />
          </AppContext.Provider>
        </DispatchContext.Provider>
      </Route>
      <Route path="/mfa-enroll">
        <MFAEnRoll />
      </Route>
      <Route path="/mfa-verify">
        <MFAVerify />
      </Route>
      <Route path="/appointment/:id">
        <DispatchContext.Provider value={taDispatch}>
          <AppContext.Provider value={taState}>
            <TrackAppointment />
          </AppContext.Provider>
        </DispatchContext.Provider>
      </Route>
      <Route path="/apt/:id/confirmation">
        <AppointmentConfirm />
      </Route>
      <Route path="/public/file/:token">
        <PublicSecureFilePage />
      </Route>
      <Route path="/public/global-file/:token">
        <PublicGlobalSecureFilePage />
      </Route>
    </Switch>
  );
};

export default AppContainer;
